import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Persist scroll positions between sibling entities that belong to the same
 * platform + level while resetting when the platform or level changes.
 */
const usePlatformScrollPersistence = ({ platform, level, platformKey }) => {
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const scrollRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const previousSelectionRef = useRef({
    platform,
    level,
  });
  const shouldRestoreRef = useRef(false);
  const isInitialRenderRef = useRef(true);
  const isTransitioningRef = useRef(false);

  // Track selection changes and decide when scroll can safely be restored
  useLayoutEffect(() => {
    if (!platform || !level || !platformKey) {
      previousSelectionRef.current = { platform, level };
      return;
    }

    if (isInitialRenderRef.current) {
      previousSelectionRef.current = { platform, level };
      isInitialRenderRef.current = false;
      return;
    }

    const { platform: prevPlatform, level: prevLevel } =
      previousSelectionRef.current;
    const samePlatform = prevPlatform === platform;
    const sameLevel = prevLevel === level;
    const canRestore =
      samePlatform && sameLevel && scrollPositionRef.current !== 0;

    shouldRestoreRef.current = canRestore;

    if (canRestore) {
      setIsRestoringScroll(true);
      isTransitioningRef.current = true;
    } else {
      shouldRestoreRef.current = false;
      scrollPositionRef.current = 0;
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
      isTransitioningRef.current = false;
      setIsRestoringScroll(false);
    }

    previousSelectionRef.current = { platform, level };
  }, [platform, level, platformKey]);

  const onScroll = (event) => {
    if (event.target && event.target === scrollRef.current) {
      // Ignore the transient zero that appears when containers collapse mid-transition
      if (isTransitioningRef.current && event.target.scrollTop === 0) {
        return;
      }
      scrollPositionRef.current = event.target.scrollTop;
    }
  };

  // Restore scroll after the new component has been rendered
  useEffect(() => {
    if (!shouldRestoreRef.current || !scrollRef.current) {
      isTransitioningRef.current = false;
      setIsRestoringScroll(false);
      return;
    }

    const restore = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollPositionRef.current;
      }
    };

    restore();
    const timeouts = [
      setTimeout(restore, 25),
      setTimeout(restore, 100),
      setTimeout(restore, 150),
      setTimeout(() => {
        restore();
        shouldRestoreRef.current = false;
        isTransitioningRef.current = false;
        setIsRestoringScroll(false);
      }, 200),
    ];

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [platformKey, level]);

  return {
    onScroll,
    isRestoringScroll,
    scrollRef,
  };
};

export default usePlatformScrollPersistence;
